/**
 * The lines a new Team is created with, and where each one starts.
 *
 * Two pure answers the create request is built from: which kinds a create
 * sends, in the order a ticket walks them, and where each starts from what
 * the person chose (`defaultLines`). The one-screen create page sends them
 * for The backlog, and "Start a Team on my tickets" for My tickets.
 */

import type { JobKindId } from "@yagni/shared";
import { JOB_STAGES, JOB_STAGE_OF_KIND } from "@yagni/shared";

/**
 * Which kinds a create sends, as a membership question and nothing more.
 *
 * A subset on purpose: `qa.sign_off_release` and the chronicler kinds beyond
 * the Brief are added on the Team page once the Team has a repository
 * environment or a channel to post in. ORDER is the catalog's (`JOB_STAGES`,
 * `JOB_STAGE_OF_KIND`), never a second hand-kept list. `as const satisfies`
 * is the compile-time proof that every id is a real catalog kind.
 */
const OFFERED_ON_CREATE_IDS = [
  "pm.propose",
  "staff.plan",
  "builder.build",
  "builder.fix_round",
  "reviewer.review_pr",
  "qa.verify_pr",
  "builder.merge",
  "chronicler.brief",
] as const satisfies readonly JobKindId[];

export type CreateTeamLineKind = (typeof OFFERED_ON_CREATE_IDS)[number];

const OFFERED_ON_CREATE: ReadonlySet<string> = new Set(OFFERED_ON_CREATE_IDS);

export function isCreateTeamLineKind(kind: string): kind is CreateTeamLineKind {
  return OFFERED_ON_CREATE.has(kind);
}

/** The offered kinds in the catalog's own declaration order. */
const OFFERED_IN_CATALOG_ORDER: readonly CreateTeamLineKind[] =
  Object.keys(JOB_STAGE_OF_KIND).filter(isCreateTeamLineKind);

/**
 * The kinds a create sends, in stage order (`JOB_STAGES`) and in catalog
 * order inside each stage.
 */
export const CREATE_TEAM_LINE_KINDS: readonly CreateTeamLineKind[] = JOB_STAGES.flatMap(
  (stage) => OFFERED_IN_CATALOG_ORDER.filter((kind) => JOB_STAGE_OF_KIND[kind] === stage.id),
);

/**
 * What the person has already said, as the lines read it. `backlogMode` is
 * the effective mode, and `trackerPicked` is whether a backlog is actually
 * attached, because "from a backlog" with nothing picked is not a reason to
 * switch a propose line on.
 */
export interface CreateTeamLineFacts {
  githubOn: boolean;
  backlogMode: "proactive" | "backlog";
  trackerPicked: boolean;
  /** Whether this workspace may switch Fletcher on. QA starts off either way. */
  fletcherAvailable: boolean;
}

/**
 * Where each offered line starts, from the facts alone.
 *
 * ON: Bailey proposes (the Team is proactive, or a backlog is attached);
 * Reeve plans, Wright builds and fixes, and Proctor reviews, all of them once
 * GitHub is on. Reeve is on whenever Wright is, so a code Team always has a
 * planner (ADR-0049). Everything else starts OFF: merge stays with a person, QA is never auto-assigned, and the Brief has
 * nowhere to post until a channel is picked.
 */
export function defaultLines(
  facts: CreateTeamLineFacts,
): Record<CreateTeamLineKind, boolean> {
  const codeIsOn = facts.githubOn;
  return {
    "pm.propose": facts.backlogMode === "proactive" || facts.trackerPicked,
    "staff.plan": codeIsOn,
    "builder.build": codeIsOn,
    "builder.fix_round": codeIsOn,
    "reviewer.review_pr": codeIsOn,
    "qa.verify_pr": false,
    "builder.merge": false,
    "chronicler.brief": false,
  };
}
